import classNames from "classnames";
import { Button } from "components/DesignSystem/Button/Button";
import ChatCard from "components/DesignSystem/ChatCard/v2/ChatCard";
import { DocumentRequestedHeader } from "components/icons/DocumentRequestedHeader";
import { ReactNode } from "react";
import { useDispatch } from "react-redux";
import { openRequestDocumentsModal } from "store/slices/requestDocumentsInChat";
import TicketResolve from "static/images/TicketResolve.svg";
import { ActionItemArchivedMessage } from "components/MessageBubble/ActionItemArchivedMessage";
import { pluralize } from "utils/pluralize";

type DocumentRequested = {
  uuid: string;
  type: string;
  is_uploaded: string;
  other_name?: string;
  description?: string;
};

type CustomData = {
  header: string;
  metadata: {
    document_request_id: string;
    requested_documents: DocumentRequested[];
  };
};

export const RequestDocumentsChatCard = ({
  children,
  channelId,
  customData,
  groupId,
  entityId,
  size,
  isDeprecated,
}: {
  children: ReactNode;
  customData: CustomData;
  channelId: string;
  groupId: string;
  entityId?: string;
  size?: "regular" | "block";
  isDeprecated: boolean;
}) => {
  const { requested_documents, document_request_id } = customData.metadata;

  const dispatch = useDispatch();

  const onUploadClick = () =>
    dispatch(
      openRequestDocumentsModal({
        isModalOpen: true,
        channelId: channelId,
        groupId: groupId,
        entityId: entityId,
        documentsRequest: requested_documents,
        requestId: document_request_id,
      })
    );

  const uploadedCount = requested_documents.filter((d) => d.is_uploaded).length;

  const allDocumentsAreUploaded = uploadedCount === requested_documents.length;

  const uploadProgress = `${uploadedCount}/${
    requested_documents.length
  } ${pluralize(
    requested_documents.length,
    "Document",
    "Documents",
    false
  )} uploaded`;

  return (
    <div
      className={classNames("t-flex t-flex-col t-gap-4", {
        "t-w-full": size === "block",
      })}
    >
      <ChatCard.Content addMargin={false}>
        <ChatCard.Header
          icon={
            <span className="t-text-text-30 t-flex t-items-center">
              {allDocumentsAreUploaded ? (
                <img src={TicketResolve} alt="TicketResolve" />
              ) : (
                <DocumentRequestedHeader />
              )}
            </span>
          }
        >
          <span className="t-text-subtext-sm t-text-text-30">
            {uploadProgress}
          </span>
        </ChatCard.Header>
        <ChatCard.Body>
          {requested_documents.map((document) => (
            <div
              className="t-flex t-gap-3 t-items-center t-py-3 t-border-0 t-border-t t-border-dashed t-border-neutral-10 first:t-border-t-0 first:t-pt-0"
              key={document.uuid}
            >
              <div className="t-flex t-flex-col t-gap-0.5 t-flex-1">
                <div className="t-text-subtext-sm t-text-text-60">
                  {document.other_name || document.type}
                </div>
                {document.description && (
                  <div className="t-text-body-sm t-text-i-text-light t-line-clamp-2">
                    {document.description}
                  </div>
                )}
              </div>
              {document.is_uploaded && (
                <img
                  src={TicketResolve}
                  alt="Uploaded"
                  className="t-size-3 t-shrink-0 t-pointer-events-none t-self-center"
                />
              )}
            </div>
          ))}
          {isDeprecated ? (
            <ActionItemArchivedMessage />
          ) : (
            <Button
              block
              onClick={onUploadClick}
              size="small"
              customType={allDocumentsAreUploaded ? "secondary" : "primary"}
            >
              {allDocumentsAreUploaded ? "View Documents" : "Add documents"}
            </Button>
          )}
        </ChatCard.Body>
      </ChatCard.Content>
      {children}
    </div>
  );
};
